export type JobRecurrenceFrequency = 'monthly' | 'quarterly' | 'annually';

export function todayYmd(now = new Date()): string {
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function tomorrowYmd(now = new Date()): string {
  const date = new Date(now.getTime());
  date.setDate(date.getDate() + 1);
  return todayYmd(date);
}

function parseYmd(ymd: string): Date {
  const [year, month, day] = ymd.slice(0, 10).split('-').map(Number);
  return new Date(year, month - 1, day);
}

function toYmd(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function addMonths(ymd: string, months: number): string {
  const date = parseYmd(ymd);
  date.setMonth(date.getMonth() + months);
  return toYmd(date);
}

function addYears(ymd: string, years: number): string {
  const date = parseYmd(ymd);
  date.setFullYear(date.getFullYear() + years);
  return toYmd(date);
}

const MONTH_NAMES: Record<string, number> = {
  january: 1,
  february: 2,
  march: 3,
  april: 4,
  may: 5,
  june: 6,
  july: 7,
  august: 8,
  september: 9,
  october: 10,
  november: 11,
  december: 12,
  jan: 1,
  feb: 2,
  mar: 3,
  apr: 4,
  jun: 6,
  jul: 7,
  aug: 8,
  sep: 9,
  oct: 10,
  nov: 11,
  dec: 12,
};

/** Parse a date, MM-DD, month number, or month name into month (1-12) and day. */
export function parseFiscalYearStart(value: unknown): { month: number; day: number } | null {
  if (value == null || value === '') return null;
  const raw = String(value).trim();
  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return { month: Number(iso[2]), day: Number(iso[3]) };
  const md = raw.match(/^(\d{1,2})-(\d{1,2})$/);
  if (md) {
    const month = Number(md[1]);
    const day = Number(md[2]);
    if (month < 1 || month > 12 || day < 1 || day > 31) return null;
    return { month, day };
  }
  if (/^\d{1,2}$/.test(raw)) {
    const month = Number(raw);
    if (month < 1 || month > 12) return null;
    return { month, day: 1 };
  }
  const named = MONTH_NAMES[raw.toLowerCase()];
  if (named) return { month: named, day: 1 };
  return null;
}

function toSnakeKey(key: string): string {
  return key
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/[\s-]+/g, '_')
    .toLowerCase();
}

function isCalendarMonthKey(key: string): boolean {
  return toSnakeKey(key) === 'calendar_month';
}

function isFinancialStartKey(key: string): boolean {
  const lower = toSnakeKey(key);
  return (
    isCalendarMonthKey(key) ||
    lower.includes('start_of_financial') ||
    (lower.includes('financial') && lower.includes('start'))
  );
}

function findValueByKey(
  record: Record<string, unknown>,
  predicate: (key: string) => boolean,
): unknown {
  for (const [key, value] of Object.entries(record)) {
    if (predicate(key)) return value;
  }
  return undefined;
}

function toMmDd(parsed: { month: number; day: number }): string {
  return `${String(parsed.month).padStart(2, '0')}-${String(parsed.day).padStart(2, '0')}`;
}

export function extractFinancialStart(
  contact:
    | {
        formFieldValues?: Array<{ fieldKey: string; value: unknown }>;
        formData?: Record<string, unknown> | string | null;
        dynamicFields?: Array<{ fieldLabel?: string; value: unknown }>;
      }
    | null
    | undefined,
): string | null {
  if (!contact) return null;

  let formData = contact.formData;
  if (typeof formData === 'string') {
    try {
      formData = JSON.parse(formData) as Record<string, unknown>;
    } catch {
      formData = null;
    }
  }

  const calendarMonthValue =
    (formData && typeof formData === 'object'
      ? findValueByKey(formData, isCalendarMonthKey)
      : undefined) ??
    contact.formFieldValues?.find((field) => isCalendarMonthKey(field.fieldKey ?? ''))?.value;

  const parsedCalendarMonth = parseFiscalYearStart(calendarMonthValue);
  if (parsedCalendarMonth) return toMmDd(parsedCalendarMonth);

  for (const field of contact.formFieldValues ?? []) {
    if (isFinancialStartKey(field.fieldKey ?? '')) {
      const parsed = parseFiscalYearStart(field.value);
      if (parsed) return toMmDd(parsed);
    }
  }

  if (formData && typeof formData === 'object') {
    const value = findValueByKey(formData, isFinancialStartKey);
    const parsed = parseFiscalYearStart(value);
    if (parsed) return toMmDd(parsed);
  }

  for (const field of contact.dynamicFields ?? []) {
    const label = field.fieldLabel?.toLowerCase() ?? '';
    if (
      label.includes('calendar month') ||
      label.includes('start of financial') ||
      (label.includes('financial') && label.includes('start'))
    ) {
      const parsed = parseFiscalYearStart(field.value);
      if (parsed) return toMmDd(parsed);
    }
  }

  return null;
}

function nextQuarterStart(today: string, fiscalYearStart: string): string | null {
  const parsed = parseFiscalYearStart(fiscalYearStart);
  if (!parsed) return null;
  const todayDate = parseYmd(today);
  const year = todayDate.getFullYear();
  const candidates: Date[] = [];
  for (const offset of [0, 3, 6, 9]) {
    let monthIndex = parsed.month - 1 + offset;
    let candidateYear = year;
    while (monthIndex > 11) {
      monthIndex -= 12;
      candidateYear += 1;
    }
    candidates.push(new Date(candidateYear, monthIndex, parsed.day));
  }
  candidates.push(new Date(year + 1, parsed.month - 1, parsed.day));
  const future = candidates
    .map(toYmd)
    .filter((ymd) => ymd > today)
    .sort();
  return future[0] ?? null;
}

export function suggestRecurrenceNextDate(options: {
  frequency: JobRecurrenceFrequency;
  createdOn: string;
  fiscalYearStart?: string | null;
  today?: string;
}): string | null {
  const today = options.today ?? todayYmd();
  const createdOn = options.createdOn.slice(0, 10) || today;

  let suggested: string | null = null;
  if (options.frequency === 'monthly') {
    suggested = addMonths(createdOn, 1);
  } else if (options.frequency === 'annually') {
    suggested = addYears(createdOn, 1);
  } else {
    if (!options.fiscalYearStart) return null;
    suggested = nextQuarterStart(today, options.fiscalYearStart);
  }

  if (!suggested || suggested <= today) return null;
  return suggested;
}
